/**
 * @file LoadingTable.jsx
 * @description Componente de loading para simular o carregamento da tabela de leads
 */

import {
    Box,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Typography
} from '@mui/material';

/**
 * Componente de loading que simula a estrutura da tabela de leads
 * 
 * @param {Object} props - Propriedades do componente
 * @param {number} props.rows - Número de linhas skeleton para exibir (padrão: 5)
 * @param {string} props.variant - Variante do loading: 'skeleton' ou 'spinner' (padrão: 'skeleton')
 * @returns {JSX.Element} Componente de loading
 */
const LoadingTable = ({
    rows = 5,
    variant = 'skeleton'
}) => {
    // Se variant for 'spinner', mostra um loading centralizado simples
    if (variant === 'spinner') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                    Carregando leads...
                </Typography>
            </Box>
        );
    }

    // Variant 'skeleton' - simula a estrutura da tabela
    return (
        <TableContainer component={Paper} elevation={0}>
            <Table>
                {/* Cabeçalho da tabela com skeleton */}
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Skeleton variant="text" width="40px" height={20} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant="text" width="80px" height={20} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant="text" width="120px" height={20} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant="text" width="100px" height={20} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant="text" width="80px" height={20} />
                        </TableCell>
                        <TableCell>
                            <Skeleton variant="text" width="100px" height={20} />
                        </TableCell>
                    </TableRow>
                </TableHead>

                {/* Corpo da tabela com linhas skeleton */}
                <TableBody>
                    {Array.from(new Array(rows)).map((_, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                <Skeleton variant="text" width="30px" height={20} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width="150px" height={20} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width="120px" height={20} />
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="circular" width={8} height={8} />
                                    <Skeleton variant="text" width="80px" height={20} />
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width="70px" height={20} />
                            </TableCell>
                            <TableCell>
                                <Skeleton variant="text" width="90px" height={20} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Indicador de carregamento adicional */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2,
                    gap: 1
                }}
            >
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                    Aplicando filtros...
                </Typography>
            </Box>
        </TableContainer>
    );
};

export default LoadingTable;